import { useState } from 'react'
import { Link } from 'react-router-dom'
import { supabase } from '../../lib/supabase'
import { compressImage } from '../../lib/imageCompression'
import { Mail, CheckCircle2 } from 'lucide-react'

const CATEGORIES = [
  ['photographer','Photographer'], ['videographer','Videographer'], ['decorator','Decorator'],
  ['mc_dj','MC / DJ'], ['caterer','Caterer'], ['aso_ebi','Aso Ebi & Fabric'], ['makeup','Makeup Artist'],
  ['bridal_fashion','Bridal Fashion'], ['venue','Venue'], ['live_band','Live Band'],
  ['cake','Cake Designer'], ['planner','Event Planner'],
]
const csv = s => (s || '').split(',').map(x => x.trim()).filter(Boolean)
const safeName = n => n.replace(/[^a-zA-Z0-9.]/g, '_')

const Label = ({ children, hint }) => (
  <label style={{ display:'block', fontFamily:'Nunito Sans, sans-serif', fontWeight:600, fontSize:'0.8rem', color:'#1A1A1A', margin:'0 0 0.35rem' }}>
    {children}{hint && <span style={{ fontWeight:400, color:'#9B9B9B' }}> — {hint}</span>}
  </label>
)
const inputStyle = { width:'100%', fontFamily:'Nunito Sans, sans-serif', fontSize:'0.875rem', border:'1px solid #E5E0D8', borderRadius:'0.75rem', padding:'0.7rem 0.9rem', outline:'none', boxSizing:'border-box' }
const Section = ({ title, children }) => (
  <div style={{ marginBottom:'1.75rem' }}>
    <h3 style={{ fontFamily:'Gibs, serif', fontWeight:400, fontSize:'1.1rem', margin:'0 0 0.875rem', color:'#1A1A1A' }}>{title}</h3>
    <div style={{ display:'flex', flexDirection:'column', gap:'0.875rem' }}>{children}</div>
  </div>
)

export default function BecomeVendorPage() {
  const [f, setF] = useState({
    business_name:'', category:'', city:'', country:'Nigeria', description:'', vendor_vow:'',
    starting_price:'', price_currency:'NGN', budget_min:'', budget_max:'',
    style_tags:'', service_areas:'', specialties:'', languages:'', capacity_max:'', years_active:'',
    instagram:'', website:'', contact_email:'', contact_phone:'',
  })
  const [refs, setRefs] = useState([
    { name:'', contact:'', relationship:'' }, { name:'', contact:'', relationship:'' }, { name:'', contact:'', relationship:'' },
  ])
  const [portfolio, setPortfolio] = useState([])
  const [docs, setDocs] = useState([])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [done, setDone] = useState(false)

  const set = (k, v) => setF(p => ({ ...p, [k]: v }))
  const setRef = (i, k, v) => setRefs(p => p.map((r, j) => j === i ? { ...r, [k]: v } : r))

  async function upload(bucket, files, returnPublic) {
    const out = []
    for (const [i, raw] of files.entries()) {
      let file = raw
      try { file = await compressImage(raw) } catch { file = raw }   // images shrink; PDFs/docs pass through untouched
      const path = `applications/${Date.now()}-${i}-${safeName(file.name)}`
      const { error } = await supabase.storage.from(bucket).upload(path, file)
      if (error) throw new Error(`Upload failed: ${error.message}`)
      out.push(returnPublic ? supabase.storage.from(bucket).getPublicUrl(path).data.publicUrl : path)
    }
    return out
  }

  async function submit(e) {
    e.preventDefault()
    setError('')
    if (!f.business_name.trim()) return setError('Business name is required.')
    if (!f.category) return setError('Please choose a category.')
    if (portfolio.length < 3) return setError('Please upload at least 3 portfolio images.')
    if (portfolio.length > 8) return setError('Please upload no more than 8 portfolio images.')
    setBusy(true)
    try {
      const gallery_images = await upload('aisle-portfolio', portfolio, true)
      const verification_documents = docs.length ? await upload('aisle-docs', docs, false) : []
      const payload = {
        business_name: f.business_name, category: f.category, city: f.city, country: f.country,
        description: f.description, vendor_vow: f.vendor_vow,
        starting_price: f.starting_price, price_currency: f.price_currency,
        budget_min: f.budget_min, budget_max: f.budget_max,
        style_tags: csv(f.style_tags), service_areas: csv(f.service_areas),
        specialties: csv(f.specialties), languages: csv(f.languages),
        capacity_max: f.capacity_max, years_active: f.years_active,
        social_links: { ...(f.instagram && { instagram: f.instagram }), ...(f.website && { website: f.website }) },
        contact_email: f.contact_email, contact_phone: f.contact_phone,
        references_list: refs.filter(r => r.name.trim()),
        gallery_images, verification_documents,
      }
      const { error } = await supabase.functions.invoke('aisle-apply', { body: payload })
      if (error) throw error
      setDone(true)
      window.scrollTo(0, 0)
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.')
    }
    setBusy(false)
  }

  if (done) return (
    <div style={{ minHeight:'100vh', background:'#FAFAF8', display:'flex', alignItems:'center', justifyContent:'center', padding:'2rem 1rem' }}>
      <div className="card" style={{ maxWidth:460, width:'100%', padding:'2.5rem', textAlign:'center' }}>
        <CheckCircle2 size={44} color="#9BAF9B" style={{ margin:'0 auto 1rem', display:'block' }} />
        <h2 style={{ fontFamily:'Gibs, serif', fontWeight:600, fontSize:'1.5rem', margin:'0 0 0.75rem' }}>Thank you!</h2>
        <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.9rem', color:'#6B6B6B', lineHeight:1.7, margin:'0 0 1.5rem' }}>
          Our team reviews applications within 3–5 business days. We'll email you once your profile is verified and live on Okoiya Aisle.
        </p>
        <Link to="/" style={{ fontFamily:'Nunito Sans, sans-serif', fontWeight:700, fontSize:'0.85rem', color:'#C4826A', textDecoration:'none' }}>← Back to Okoiya</Link>
      </div>
    </div>
  )

  return (
    <div style={{ minHeight:'100vh', background:'#FAFAF8', padding:'2rem 1rem' }}>
      <div style={{ maxWidth:640, margin:'0 auto' }}>
        <Link to="/" style={{ display:'block', marginBottom:'1.5rem' }}>
          <img src="/okoiya-logo.png" alt="Okoiya" style={{ height:40 }} />
        </Link>
        <h1 style={{ fontFamily:'Gibs, serif', fontWeight:600, fontSize:'2rem', margin:'0 0 0.4rem' }}>Join Okoiya Aisle</h1>
        <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.92rem', color:'#6B6B6B', margin:'0 0 2rem', lineHeight:1.6 }}>
          Get in front of couples planning their weddings. Apply below — our team verifies every vendor before they go live.
        </p>

        <form onSubmit={submit} className="card" style={{ padding:'1.75rem' }}>
          <Section title="About your business">
            <div><Label>Business name</Label>
              <input style={inputStyle} value={f.business_name} onChange={e => set('business_name', e.target.value)} required /></div>
            <div><Label>Category</Label>
              <select style={inputStyle} value={f.category} onChange={e => set('category', e.target.value)} required>
                <option value="">Choose a category…</option>
                {CATEGORIES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select></div>
            <div style={{ display:'flex', gap:'0.875rem', flexWrap:'wrap' }}>
              <div style={{ flex:'1 1 160px' }}><Label>City</Label><input style={inputStyle} value={f.city} onChange={e => set('city', e.target.value)} placeholder="e.g. Lagos" /></div>
              <div style={{ flex:'1 1 160px' }}><Label>Country</Label><input style={inputStyle} value={f.country} onChange={e => set('country', e.target.value)} /></div>
            </div>
            <div><Label>Description</Label>
              <textarea style={{ ...inputStyle, resize:'vertical' }} rows={3} value={f.description} onChange={e => set('description', e.target.value)} /></div>
            <div><Label hint="In your own words — what do you promise every couple who books you?">Vendor Vow</Label>
              <textarea style={{ ...inputStyle, resize:'vertical' }} rows={3} value={f.vendor_vow} onChange={e => set('vendor_vow', e.target.value)} /></div>
          </Section>

          <Section title="Pricing & matching details">
            <div style={{ display:'flex', gap:'0.875rem', flexWrap:'wrap' }}>
              <div style={{ flex:'1 1 120px' }}><Label>Starting price</Label><input style={inputStyle} type="number" value={f.starting_price} onChange={e => set('starting_price', e.target.value)} /></div>
              <div style={{ flex:'0 1 100px' }}><Label>Currency</Label>
                <select style={inputStyle} value={f.price_currency} onChange={e => set('price_currency', e.target.value)}>
                  {['NGN','USD','GBP','EUR'].map(c => <option key={c}>{c}</option>)}
                </select></div>
            </div>
            <div style={{ display:'flex', gap:'0.875rem', flexWrap:'wrap' }}>
              <div style={{ flex:'1 1 120px' }}><Label hint="optional">Budget min</Label><input style={inputStyle} type="number" value={f.budget_min} onChange={e => set('budget_min', e.target.value)} /></div>
              <div style={{ flex:'1 1 120px' }}><Label hint="optional">Budget max</Label><input style={inputStyle} type="number" value={f.budget_max} onChange={e => set('budget_max', e.target.value)} /></div>
            </div>
            <div><Label hint="comma-separated, e.g. luxury, documentary, traditional">Style tags</Label>
              <input style={inputStyle} value={f.style_tags} onChange={e => set('style_tags', e.target.value)} /></div>
            <div><Label hint="comma-separated cities you serve">Service areas</Label>
              <input style={inputStyle} value={f.service_areas} onChange={e => set('service_areas', e.target.value)} placeholder="Lagos, Abuja" /></div>
            <div><Label hint="comma-separated">Specialties</Label>
              <input style={inputStyle} value={f.specialties} onChange={e => set('specialties', e.target.value)} /></div>
            <div><Label hint="comma-separated">Languages</Label>
              <input style={inputStyle} value={f.languages} onChange={e => set('languages', e.target.value)} placeholder="English, Yoruba" /></div>
            <div style={{ display:'flex', gap:'0.875rem', flexWrap:'wrap' }}>
              <div style={{ flex:'1 1 120px' }}><Label hint="optional">Max capacity</Label><input style={inputStyle} type="number" value={f.capacity_max} onChange={e => set('capacity_max', e.target.value)} /></div>
              <div style={{ flex:'1 1 120px' }}><Label hint="optional">Years active</Label><input style={inputStyle} type="number" value={f.years_active} onChange={e => set('years_active', e.target.value)} /></div>
            </div>
          </Section>

          <Section title="Portfolio & verification">
            <div><Label hint="3–8 images">Portfolio images</Label>
              <input type="file" accept="image/*" multiple onChange={e => setPortfolio(Array.from(e.target.files || []))} style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.82rem' }} />
              {portfolio.length > 0 && <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.72rem', color:'#9BAF9B', margin:'0.4rem 0 0' }}>{portfolio.length} image{portfolio.length===1?'':'s'} selected</p>}
            </div>
            <div><Label hint="CAC certificate / business registration — kept private">Verification documents</Label>
              <input type="file" accept="image/*,application/pdf" multiple onChange={e => setDocs(Array.from(e.target.files || []))} style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.82rem' }} />
              {docs.length > 0 && <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.72rem', color:'#9BAF9B', margin:'0.4rem 0 0' }}>{docs.length} file{docs.length===1?'':'s'} selected</p>}
            </div>
          </Section>

          <Section title="References — 3 contacts">
            {refs.map((r, i) => (
              <div key={i} style={{ display:'flex', gap:'0.5rem', flexWrap:'wrap' }}>
                <input style={{ ...inputStyle, flex:'1 1 120px' }} placeholder="Name" value={r.name} onChange={e => setRef(i, 'name', e.target.value)} />
                <input style={{ ...inputStyle, flex:'1 1 120px' }} placeholder="Phone / email" value={r.contact} onChange={e => setRef(i, 'contact', e.target.value)} />
                <input style={{ ...inputStyle, flex:'1 1 120px' }} placeholder="Relationship (e.g. past client)" value={r.relationship} onChange={e => setRef(i, 'relationship', e.target.value)} />
              </div>
            ))}
          </Section>

          <Section title="Contact & social">
            <div style={{ display:'flex', gap:'0.875rem', flexWrap:'wrap' }}>
              <div style={{ flex:'1 1 160px' }}><Label>Contact email</Label><input style={inputStyle} type="email" value={f.contact_email} onChange={e => set('contact_email', e.target.value)} /></div>
              <div style={{ flex:'1 1 160px' }}><Label>Contact phone</Label><input style={inputStyle} value={f.contact_phone} onChange={e => set('contact_phone', e.target.value)} /></div>
            </div>
            <div style={{ display:'flex', gap:'0.875rem', flexWrap:'wrap' }}>
              <div style={{ flex:'1 1 160px' }}><Label hint="optional">Instagram</Label><input style={inputStyle} value={f.instagram} onChange={e => set('instagram', e.target.value)} placeholder="@yourhandle" /></div>
              <div style={{ flex:'1 1 160px' }}><Label hint="optional">Website</Label><input style={inputStyle} value={f.website} onChange={e => set('website', e.target.value)} /></div>
            </div>
          </Section>

          {error && (
            <div style={{ background:'rgba(196,130,106,0.1)', border:'1px solid rgba(196,130,106,0.3)', borderRadius:'0.5rem', padding:'0.65rem 0.875rem', marginBottom:'1rem' }}>
              <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.8rem', color:'#A86650', margin:0 }}>{error}</p>
            </div>
          )}
          <button type="submit" disabled={busy}
            style={{ width:'100%', display:'flex', alignItems:'center', justifyContent:'center', gap:'0.5rem',
              background: busy ? '#D4C4BC' : '#C4826A', color:'#fff', border:'none', borderRadius:100,
              padding:'0.95rem', fontFamily:'Nunito Sans, sans-serif', fontWeight:700, fontSize:'0.95rem', cursor: busy ? 'default' : 'pointer' }}>
            <Mail size={16} /> {busy ? 'Submitting…' : 'Submit application'}
          </button>
        </form>
      </div>
    </div>
  )
}
